import { useCallback, useMemo } from 'react'

import _uniqBy from 'lodash/uniqBy'

import { ROOT_FOLDER, ROOT_FOLDERS } from './fileManagerSlice'
import { CUSTOMER_FOLDER, DELETED_FOLDER } from '@/features/FileManager/FileManager'
import { useCurrentApplication } from '@/graphql/services/application'
import {
  useListCustomerDocumentByAppExternalIdQuery,
  useListUserDocumentByAppExternalIdQuery,
} from '@/services/documents'

export const useDocuments = () => {
  const { data: application } = useCurrentApplication()
  const { data: userDocs } = useListUserDocumentByAppExternalIdQuery(application?.externalId, {
    skip: !application?.uuid,
  })

  const { data: customerDocs } = useListCustomerDocumentByAppExternalIdQuery(
    application?.externalId,
    {
      skip: !application?.uuid,
    }
  )

  // Check if the tag has a parent and child folder (PARENT/CHILD).
  // If have this structure, return the tag unchanged
  // Else, set ROOT_FOLDER as default parent
  const normalizeTag = (tag: string) => {
    const splitedFolder = tag.split('/')

    return splitedFolder.length === 1 && !ROOT_FOLDERS.includes(tag) && tag !== DELETED_FOLDER
      ? `${ROOT_FOLDER}/${tag}`
      : tag
  }

  const getUserDocs = useCallback(() => userDocs, [userDocs])

  const data = useMemo(() => {
    let output = []

    const formattedCustomerDocs = []

    customerDocs?.forEach(({ files, id, uuid, size, created_at, modified_at, deleted_at }) => {
      files.forEach(({ file, original_filename }, index) => {
        formattedCustomerDocs.push({
          id,
          uuid: uuid + '-' + index,
          fileName: original_filename,
          filePath: file,
          readonly: true,
          size,
          created_at: created_at,
          modifiedAt: modified_at,
          deletedAt: deleted_at,
          tag: `${ROOT_FOLDER}/${CUSTOMER_FOLDER}`,
        })
      })
    })

    const formattedUserDocs =
      userDocs?.map(
        ({
          id,
          uuid,
          original_filename,
          single_file,
          download_url,
          size,
          modified_at,
          created_at,
          deleted_at,
          tag,
        }) => ({
          id,
          uuid,
          fileName: original_filename,
          filePath: single_file,
          downloadPath: download_url,
          readonly: false,
          size,
          created_at: created_at,
          modifiedAt: modified_at,
          deletedAt: deleted_at,
          tag: tag === 'null' || !tag ? ROOT_FOLDER : normalizeTag(tag),
        })
      ) || []

    output = [...formattedCustomerDocs, ...formattedUserDocs, ...output]

    return _uniqBy(output, (doc) => doc.uuid)
  }, [userDocs, customerDocs])

  return { data, getUserDocs }
}

export default useDocuments
